import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { QueueAction } from '@rossum/api-client/schemas';
import { IconCustomBoltPause } from '@rossum/ui/icons/tabler';
import { Autocomplete, TextField } from '@rossum/ui/material';
import { getUnsafe } from '../../../lib/helpers';
import { getSchemaFields } from '../../formulas/FormulaEditor/helpers';
import { createForm } from './createForm';
import { createHeader } from './createHeader';

const automationBlockerActionPayloadSchema = (intl?: IntlShape) =>
  z.object({
    content: z
      .string()
      .default('')
      .refine(
        val => val.length > 0,
        intl?.formatMessage({
          id: 'features.queueSettings.rules.actions.automationBlocker.payload.content.required',
        })
      ),
    schema_id: z.string().optional(),
  });

const AutomationBlockerActionForm = createForm(
  ({ value, onChange, schemaContent, errors }) => {
    const intl = useIntl();
    const schemaFieldOptions = useMemo(
      () =>
        getSchemaFields(schemaContent).filter(
          field => field.category === 'datapoint'
        ),
      [schemaContent]
    );
    const schemaField = useMemo(
      () => schemaFieldOptions.find(f => f.id === value.schema_id),
      [schemaFieldOptions, value.schema_id]
    );

    return (
      <>
        <Autocomplete
          size="small"
          options={schemaFieldOptions}
          getOptionLabel={option => `${option.label} (${option.id})`}
          value={schemaField}
          onChange={(_e, v) => {
            if (v) {
              onChange({ ...value, schema_id: v.id });
            } else {
              onChange({ ...value, schema_id: undefined });
            }
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={intl.formatMessage({
                id: 'features.queueSettings.rules.actions.automationBlocker.form.placement',
              })}
              placeholder={intl.formatMessage({
                id: 'features.queueSettings.rules.actions.automationBlocker.form.placementNone',
              })}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
        <TextField
          label={intl.formatMessage({
            id: 'features.queueSettings.rules.actions.automationBlocker.form.content',
          })}
          size="small"
          multiline
          maxRows={10}
          value={value.content}
          error={getUnsafe(errors, 'content') !== undefined}
          helperText={getUnsafe(errors, 'content.message')?.toString()}
          onChange={e => onChange({ ...value, content: e.currentTarget.value })}
        />
      </>
    );
  },
  (intl?: IntlShape) => automationBlockerActionPayloadSchema(intl)
);

const AutomationBlockerActionHeader = createHeader(
  ({ value, schemaContent, RenderComponent }) => {
    const intl = useIntl();
    const schemaFieldOptions = useMemo(
      () => getSchemaFields(schemaContent),
      [schemaContent]
    );
    const schemaField = useMemo(
      () => schemaFieldOptions.find(f => f.id === value.schema_id),
      [schemaFieldOptions, value.schema_id]
    );
    return (
      <RenderComponent
        entries={[
          {
            key: 'placement',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.actions.automationBlocker.form.placement',
            }),
            value:
              schemaField?.label ??
              value.schema_id ??
              intl.formatMessage({
                id: 'features.queueSettings.rules.actions.message.form.placementHeader',
              }),
            sx: {
              maxWidth: 200,
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            },
          },
          {
            key: 'content',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.actions.automationBlocker.form.content',
            }),
            value: value.content,
            sx: {
              maxWidth: 400,
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            },
          },
        ]}
      />
    );
  },
  (intl?: IntlShape) => automationBlockerActionPayloadSchema(intl)
);

export const automationBlockerAction = {
  icon: IconCustomBoltPause,
  intlKey: 'automationBlocker' as const,
  resolveAction: (a: QueueAction) => a.type === 'add_automation_blocker',
  serialized: { type: 'add_automation_blocker' as const },
  groupIntlKey: 'automation' as const,
  form: AutomationBlockerActionForm,
  header: AutomationBlockerActionHeader,
  supportedEvents: ['validation'] as const,
  payloadSchema: (intl?: IntlShape) =>
    automationBlockerActionPayloadSchema(intl),
};
